import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XCloseIcon } from '@rsos/base-assets/icons';
import { Text, Box, Button } from '@rsos/base-components';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import {
  CloseButton,
  ListButton,
  MessageDisplay,
  SampleMessage,
  SearchBar,
  SearchInput,
  Titles,
} from './AllMessages.styles';
import { getMatchingPreconfigured } from './utils';

interface AllMessagesInterface {
  preconfiguredMessages: PreconfiguredMessageInterface[];
  onCopyToChat?: (preconfiguredMessages: PreconfiguredMessageInterface) => void;
  handleSelectMessage?: (
    preconfiguredMessage: PreconfiguredMessageInterface,
  ) => void;
  onClose?: () => void;
}

const AllMessages = ({
  preconfiguredMessages = [],
  handleSelectMessage,
  onCopyToChat,
  onClose,
}: AllMessagesInterface) => {
  const [selectedMessage, setSelectedMessage] = useState(
    preconfiguredMessages[0],
  );
  const [searchedMessage, setSearchedMessage] = useState('');

  const matchingPreconfigured = useMemo(
    () => getMatchingPreconfigured(preconfiguredMessages, searchedMessage),
    [preconfiguredMessages, searchedMessage],
  );

  const handleClick = (preconfiguredMessage: PreconfiguredMessageInterface) => {
    setSelectedMessage(preconfiguredMessage);

    if (handleSelectMessage) {
      handleSelectMessage(preconfiguredMessage);
    }
  };

  const handleCopyToChat = () => {
    if (onCopyToChat) {
      onCopyToChat(selectedMessage);
    }
  };

  return (
    <Box
      width="100%"
      height="100%"
      backgroundColor="background"
      padding="4px 16px 16px"
      borderRadius="5px"
    >
      <Box horizontal justify="space-between" align="center" marginBottom="8px">
        <Text color="primaryText">All Messages</Text>
        <CloseButton btnType="invisible" onClick={onClose}>
          <XCloseIcon />
        </CloseButton>
      </Box>
      <Box horizontal grow overflowY="auto">
        <Box flex="1" marginRight="8px">
          <SearchBar>
            <MagnifyingGlassIcon
              color="secondaryText"
              height="16px"
              width="16px"
            />
            <SearchInput
              type="text"
              placeholder="Search Messages"
              onChange={e => setSearchedMessage(e.target.value)}
            />
          </SearchBar>
          <Titles>
            {matchingPreconfigured.map(preconfiguredMessage => (
              <ListButton
                key={preconfiguredMessage.title}
                isSelected={
                  selectedMessage.title === preconfiguredMessage.title
                }
                onClick={() => handleClick(preconfiguredMessage)}
              >
                <Text color="primaryText" size="normal">
                  {preconfiguredMessage.title}
                </Text>
              </ListButton>
            ))}
          </Titles>
        </Box>
        <MessageDisplay>
          <Text color="secondaryText" fontWeight="bold" size="small">
            Message Text
          </Text>
          {selectedMessage?.body && (
            <SampleMessage>
              <Text
                data-name="selected-message"
                size="normal"
                color="background"
              >
                {selectedMessage.body}
              </Text>
            </SampleMessage>
          )}
        </MessageDisplay>
      </Box>
      <Box align="flex-end" marginTop="8px">
        <Button noMargin onClick={handleCopyToChat}>
          Copy to Chat
        </Button>
      </Box>
    </Box>
  );
};

export default AllMessages;
